// src/plugins/sales-forecast/components/CloneNeighborhoodControl.tsx
import React, { useState, useEffect } from "react";
import neighborhoodList from "../neighborhoods.json";
import marketList from "../markets.json";
import schoolDistrictList from "../school_districts.json";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface CloneEntry {
  id: string;
  title: string;
  cloneType: "Neighborhood" | "Market" | "School District";
  defaults: {
    interestRate: number;
    interestRateThreeMonthChange: number;
    interestRateSixMonthChange: number;
    interestRateNineMonthChange: number;
    interestRateTwelveMonthChange: number;
    interestRateTwoYearChange: number;
    initialSpecs: number;
    preSolds: number;
    specsToStart: number;
    perSpecsSold: number;
    avgPriceMin: number;
    avgPriceMax: number;
    availableLots: number;
    squareFootMin: number;
    squareFootMax: number;
    composition: number;
    amenities: string[];
    threeMonthPriceChange: number;
    sixMonthPriceChange: number;
    nineMonthPriceChange: number;
    twelveMonthPriceChange: number;
    twoYearPriceChange: number;
    bedrooms: number;
    baths: number;
    stories: number;
    yearBuilt: number;
    garageType: string;
    garageSpaces: number;
    city: string;
    lotType: string;
    listingDay: number;
    listingMonth: string;
    activeComps: number;
    cloneDate?: string;
  };
}

interface CloneNeighborhoodControlProps {
  onClone: (entry: CloneEntry) => void;
}

export const CloneNeighborhoodControl: React.FC<CloneNeighborhoodControlProps> = ({
  onClone,
}) => {
  const theme = useSystemTheme();

  const today = new Date().toISOString().split("T")[0];
  const [cloneType, setCloneType] = useState<
    "Neighborhood" | "Market" | "School District"
  >("Neighborhood");
  const [entries, setEntries] = useState<CloneEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(today);

  /** Helper: safely map JSON data to CloneEntry[] */
  const buildList = (
    source: any[],
    type: CloneEntry["cloneType"]
  ): CloneEntry[] =>
    source.map((n) => ({
      id: n.id,
      title: n.title,
      cloneType: type,
      defaults: {
        ...n.defaults,
        amenities: [...n.defaults.amenities],
        cloneDate: selectedDate, // store latest date by default
      },
    }));

  /** Effect: rebuild entries whenever cloneType changes (not date!) */
  useEffect(() => {
    let sourceList: any[] = [];

    if (cloneType === "Neighborhood") sourceList = neighborhoodList;
    else if (cloneType === "Market") sourceList = marketList;
    else if (cloneType === "School District") sourceList = schoolDistrictList;

    const list = buildList(sourceList, cloneType);
    setEntries(list);

    // preserve previous selection if it exists in new list
    const firstId = list.length ? list[0].id : "";
    setSelectedId((prev) =>
      list.some((e) => e.id === prev) ? prev : firstId
    );

    // Send initial clone with preserved or first item
    const selectedEntry =
      list.find((e) => e.id === selectedId) || list[0] || null;
    if (selectedEntry) {
      requestAnimationFrame(() =>
        onClone({
          ...selectedEntry,
          defaults: { ...selectedEntry.defaults, cloneDate: selectedDate },
        })
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneType]); // ✅ only runs when cloneType changes

  /** Handle selecting a different neighborhood/market/school */
  const handleCloneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    setSelectedId(newId);
    const found = entries.find((entry) => entry.id === newId);
    if (found) {
      const updated = {
        ...found,
        defaults: { ...found.defaults, cloneDate: selectedDate },
      };
      console.log("[CloneNeighborhoodControl] sending entry (clone change):", updated);
      onClone(updated);
    }
  };

  /** Handle date change (does NOT rebuild entries) */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setSelectedDate(date);

    // update currently selected entry
    const found = entries.find((entry) => entry.id === selectedId);
    if (found) {
      const updated = {
        ...found,
        defaults: { ...found.defaults, cloneDate: date },
      };
      console.log("[CloneNeighborhoodControl] sending entry (date change):", updated);
      onClone(updated);
    }
  };

  // === Theme colors ===
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const inputBg = css.getPropertyValue("--input-bg").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const mutedText = css.getPropertyValue("--muted-text-color").trim();
  const accentColor = css.getPropertyValue("--accent-color").trim();
  const dropdownBg = inputBg || "var(--subcard-bg)";

  return (
    <div
      className={`clone-neighborhood-control theme-${theme} flex flex-col space-y-3 w-full`}
      style={{
        backgroundColor: "transparent",
        color: textColor,
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
    >
      {/* === Clone Type Selector === */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneType"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          Clone From
        </label>
        <select
          id="cloneType"
          value={cloneType}
          onChange={(e) => setCloneType(e.target.value as any)}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <option value="Neighborhood">Neighborhood</option>
          <option value="Market">Market</option>
          <option value="School District">School District</option>
        </select>
      </div>

      {/* === Entity Selector === */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneSelect"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          {cloneType === "Neighborhood" && "Neighborhood Selection"}
          {cloneType === "Market" && "Market Selection"}
          {cloneType === "School District" && "School District Selection"}
        </label>

        <select
          id="cloneSelect"
          value={selectedId}
          onChange={handleCloneChange}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
            cursor: "pointer",
          }}
        >
          {entries.map((e) => (
            <option
              key={e.id}
              value={e.id}
              style={{
                backgroundColor: dropdownBg,
                color: textColor,
              }}
            >
              {e.title}
            </option>
          ))}
        </select>
      </div>

      {/* === Date Selector === */}
      <div className="flex flex-col space-y-1 w-full">
        <label
          htmlFor="cloneDate"
          className="font-medium whitespace-nowrap"
          style={{ color: mutedText }}
        >
          Forecast Start Date
        </label>
        <input
          id="cloneDate"
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className="transition-all duration-200 w-full"
          style={{
            backgroundColor: dropdownBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            padding: "0.5rem 0.75rem",
            fontSize: "0.95rem",
            fontWeight: 500,
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          }}
        />
      </div>

      {/* Inline theme styling */}
      <style>
        {`
          select.theme-${theme} option {
            background-color: ${dropdownBg} !important;
            color: ${textColor} !important;
          }
          select.theme-${theme}:focus option:checked {
            background-color: ${accentColor} !important;
            color: #fff !important;
          }
        `}
      </style>
    </div>
  );
};